import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, Menu, MenuItem } from "@mui/material";

import { changeUtilityBillStatus, getUtilityBillById } from "api";

import { EsgArrowDown } from "assets";
import "./css/style.css"

import {
  CloseButton,
  COLORS,
  DrawerView,
  Loading,
  PdfFooter,
  PdfPreview,
  RoutedTabs,
  TYPOGRAPHY_COMMON_STYLES,
  UTILITY_BILL_STATUS_TO_BACKGROUND_COLOR,
  UTILITY_BILL_STATUS_TO_MESSAGE,
} from "components";

import { useGetConfigParameter, useNotify, useUtilityBillById, useUtilityBillWSConnection } from "hooks";

import { combineDropdownOptions, getFromLocalStorage, getLoggedUserData, isFeatureEnabled, setToLocalStorage } from "utils";
import { useMutation } from "@tanstack/react-query";

import { approvalFlowStatuses, basicStatuses, notApplicableStatuses } from "./consts";
import { UTILITY_BILL_ALL_DATAENTRIES_VIEW } from "./utility-bill-allDataEntries-view/consts";
import { UTILITY_BILL_COMMENTS_VIEW } from "./utility-bill-comments-view";
import { UTILITY_BILL_FORM_VIEW } from "./utility-bill-form-view";
import { UtilityBillStatusKey } from "./types";
import { UTILITY_BILL_PAGE } from "../consts";

export const UtilityBill: React.FunctionComponent = () => {
  const { billId } = useParams<{ [UTILITY_BILL_PAGE.KEY]: string }>();
  const { data: configParameter } = useGetConfigParameter();


  const isApprovalFlowValueOn = isFeatureEnabled(configParameter, "second_layer_approval_feature");
  const isAdditionalStatusesOn = isFeatureEnabled(configParameter, "unusable_status_enabled");

  // Right below 👇 we have basicStatuses always on, that means = true;
  const dropDownStatusOption: Array<{ label: string, name: string }> = combineDropdownOptions(
    [true, isApprovalFlowValueOn, isAdditionalStatusesOn],
    [basicStatuses, approvalFlowStatuses, notApplicableStatuses],
  );
  const { data: utilityBill, isLoading, refetch } = useUtilityBillById(billId!);
  const { mutateAsync: updateBillStatus, isLoading: isStatusLoading } = useMutation(changeUtilityBillStatus);

  const [isOpen, setIsOpen] = useState(!!billId);
  const [scale, setScale] = useState(0.75);
  const [scaleChange, setScaleChange] = useState(false);
  const [thumbUpClicked, setThumbUpClicked] = useState(utilityBill && utilityBill.feedback === "Thumbs_Up");
  const [thumbDownClicked, setThumbDownClicked] = useState(utilityBill && utilityBill.feedback === "Thumbs_Down");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");

  const notify = useNotify();
  const loggedUserData = getLoggedUserData();

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (status: any) => {
    const statusChangePayload = {
      "id": billId,
      status
    }
    if (status) {
      updateBillStatus(statusChangePayload, {
        onSuccess: () => {
          setSelectedStatus(status);
        },
        onError: (e: any) => {
          notify.error(e?.response?.data)
          refetch();
        }
      })
    }
    setAnchorEl(null);
  };

  const algorithm = getFromLocalStorage("algorithm");
  const utilityBillStatus: UtilityBillStatusKey = (utilityBill as any)?.statusFilter!;

  useUtilityBillWSConnection(billId!);
  const navigate = useNavigate();
  setToLocalStorage("setid", utilityBill?.id);
  const getType = getFromLocalStorage("type") || "";
  const onClose = () => {
    let getURL: string = "";
    if (getType.includes("Upload")) {
      getURL = getFromLocalStorage("Upload_URL") || "";
    }
    if (getType.includes("Emissions")) {
      getURL = getFromLocalStorage("Emission_URL") || "";
    }
    if (getType.includes("Summary")) {
      getURL = getFromLocalStorage("Summary_URL") || "";
    }

    navigate(getURL);
    localStorage.removeItem("type");
    localStorage.removeItem("Upload_URL");

    setIsOpen((state) => !state);
  };

  useEffect(() => {
    const fetchUtilityBill = async () => {
      if (utilityBill) {
        const response = await getUtilityBillById(utilityBill.id);
        if (response.feedback === "Thumbs_Up") {
          setThumbUpClicked(true);
          setThumbDownClicked(false);
        } else if (response.feedback === "Thumbs_Down") {
          setThumbDownClicked(true);
          setThumbUpClicked(false);
        }
      }
    };

    fetchUtilityBill();
  }, [utilityBill, billId, thumbDownClicked, thumbUpClicked]);


  useEffect(() => {
    setSelectedStatus(UTILITY_BILL_STATUS_TO_MESSAGE[utilityBillStatus])
  }, [utilityBillStatus])

  if (scale <= 0.25) {
    setScale(0.75);
  }

  function checkURL(url: any) {
    return !url.includes('/Data_Entries') && !url.includes('/comments');
  }

  const isAuditor = loggedUserData?.roles?.includes("AUDITOR")
  const isDataProvider = loggedUserData?.roles?.includes("DATA_PROVIDER")
  const showDropDown = checkURL(window.location.pathname)
  const isApproved = (utilityBillStatus === "Approved (Signed Off)" || utilityBillStatus === "Approved (Locked)");

  return (
    <DrawerView isOpen={isOpen} onClose={onClose}>
      {(isLoading || isStatusLoading) ? (
        <Loading />
      ) : (
        utilityBill && (
          <Box
            sx={{
              width: "100%",
              height: "100vh",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{
                height: "100%",
                display: "flex",
              }}
            >
              <Box
                sx={{
                  width: "40%",
                  "> div": {
                    width: "100% !important",
                    height: "calc(100vh - 74px) !important",
                    "> div": {
                      width: "100% !important",
                      minWidth: "100% !important",
                      height: "100%",
                      padding: 0,
                      "> div": {
                        width: "100% !important",
                        height: "100%",
                      },
                    },
                  },
                }}
              >
                <PdfPreview
                  downloadUrl={utilityBill.downloadUrl}
                  scale={scale}
                  isScaleChange={scaleChange}
                  billId={billId}
                />
              </Box>
              <Box
                sx={{
                  width: "60%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "end",
                  padding: 3,
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    height: "34px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  {showDropDown ? <Box sx={{ display: "flex" }}>
                    <Button
                      endIcon={isApproved ? "" : <EsgArrowDown />}
                      variant="outlined"
                      disabled={isApproved || isAuditor}
                      sx={{ alignItems: "center", gap: 2, display: "flex", justifyContent: "center", borderRadius: "8px" }}
                      onClick={handleClick}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, padding: "2px 6px", borderRadius: "12px" }}>
                        <Box
                          sx={{
                            bgcolor: UTILITY_BILL_STATUS_TO_BACKGROUND_COLOR[utilityBillStatus],
                            height: "10px",
                            width: "10px",
                            borderRadius: "50%",
                          }}
                        />
                        <Box sx={{ color: "black" }}>{selectedStatus}</Box>
                      </Box>
                    </Button>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={() => handleClose(null)}
                    >
                      {dropDownStatusOption.map((status: { label: string, name: string }) => (
                        <MenuItem
                          disabled={isDataProvider && status.name === "Reviewed"}
                          sx={{
                            fontWeight:
                              TYPOGRAPHY_COMMON_STYLES.fontWeightSemiBold,
                            fontSize: 14,
                            lineHeight: "18px",
                            color: "#90959E",
                            "&:hover": {
                              backgroundColor: "#F6F8FA",
                              borderRadius: 5,
                              color: COLORS.Romance,
                            },
                            "&.Mui-selected": {
                              backgroundColor: "#F6F8FA",
                              borderRadius: 5,
                              color: COLORS.Romance,
                            },
                          }}
                          key={status.name}
                          onClick={() => handleClose(status.name)}>
                          {status.label}
                        </MenuItem>
                      ))}
                    </Menu>
                  </Box> : <Box />}
                  {(algorithm !== "IDP Fallback" ||
                    algorithm !== "IDP A/B Testing") && (
                      <CloseButton onClick={onClose} variant="outlined" />
                    )}
                </Box>
                <Box
                  sx={{
                    width: "100%",
                    height: "calc(100vh - 167px)",
                    mt: 2,
                  }}
                >
                  <RoutedTabs
                    tabs={[
                      {
                        label: UTILITY_BILL_ALL_DATAENTRIES_VIEW.TITLE,
                        to: UTILITY_BILL_ALL_DATAENTRIES_VIEW.ABSOLUTE_PATH(
                          utilityBill.id,
                        ),
                        disable: false,
                      },
                      {
                        icon: UTILITY_BILL_FORM_VIEW.ICON,
                        label: UTILITY_BILL_FORM_VIEW.TITLE,
                        to: UTILITY_BILL_FORM_VIEW.ABSOLUTE_PATH(
                          utilityBill.id,
                        ),
                        disable: false,
                      },
                      {
                        icon: UTILITY_BILL_COMMENTS_VIEW.ICON,
                        label: UTILITY_BILL_COMMENTS_VIEW.TITLE(
                          utilityBill.numberOfComments,
                        ),
                        to: UTILITY_BILL_COMMENTS_VIEW.ABSOLUTE_PATH(
                          utilityBill.id,
                        ),
                        disable: false,
                      },
                    ]}
                  />
                </Box>
              </Box>
            </Box>
            <PdfFooter
              fileName={utilityBill.filename}
              setScale={setScale}
              downloadUrl={utilityBill.downloadUrl as string}
              actionsDisabled={isLoading}
              setScaleChange={setScaleChange}
            />
          </Box>
        )
      )}
    </DrawerView>
  );
};
